import React, { useEffect, useMemo, useState } from 'react';
import { Appointment, Dentist, Patient, ClinicSettings, PaymentRecord, PaymentMethod } from '../../types';
import { Icons } from '../Icons';

interface PaymentsViewProps {
  appointments: Appointment[];
  patients: Patient[];
  dentists: Dentist[];
  clinicSettings: ClinicSettings;
  payments: PaymentRecord[];
  onRecordPayment: (payment: Omit<PaymentRecord, 'id' | 'createdAt'>) => void;
  onUpdatePayment: (payment: PaymentRecord) => void;
  onDeletePayment: (id: string) => void;
  intent?: {
    patientId?: string;
    appointmentId?: string;
    amount?: number;
  };
  onConsumeIntent?: () => void;
}

export const PaymentsView: React.FC<PaymentsViewProps> = ({
  appointments,
  patients,
  dentists,
  clinicSettings,
  payments,
  onRecordPayment,
  onUpdatePayment,
  onDeletePayment,
  intent,
  onConsumeIntent,
}) => {
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [selectedApptId, setSelectedApptId] = useState<string>('');
  const [amount, setAmount] = useState<number>(clinicSettings.defaultPaymentAmount || 0);
  const [totalDue, setTotalDue] = useState<number>(clinicSettings.defaultPaymentAmount || 0);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [note, setNote] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [historyPatientId, setHistoryPatientId] = useState<string>('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const getPatientName = (id: string) => patients.find(p => p.id === id)?.name || '';
  const editingPayment = editingId ? payments.find(p => p.id === editingId) : null;

  const filteredPatients = useMemo(
    () =>
      patients.filter(p =>
        `${p.name} ${p.email} ${p.phone}`.toLowerCase().includes(patientSearch.toLowerCase())
      ),
    [patients, patientSearch]
  );
  const patientMap = useMemo(() => new Map(filteredPatients.map(p => [p.name, p.id])), [filteredPatients]);

  const handlePatientInputChange = (value: string) => {
    setPatientSearch(value);
    if (!value.trim()) {
      setSelectedPatientId('');
      setSelectedApptId('');
      setHistoryPatientId('all');
      setAmount(clinicSettings.defaultPaymentAmount || 0);
      setTotalDue(clinicSettings.defaultPaymentAmount || 0);
      return;
    }
    const exactId = patientMap.get(value);
    if (exactId) {
      setSelectedPatientId(exactId);
      setSelectedApptId('');
      setHistoryPatientId(exactId);
      setAmount(clinicSettings.defaultPaymentAmount || 0);
      setTotalDue(clinicSettings.defaultPaymentAmount || 0);
      return;
    }
    const match = filteredPatients.find(p => p.name.toLowerCase().includes(value.toLowerCase()));
    if (match) {
      setSelectedPatientId(match.id);
      setSelectedApptId('');
      setHistoryPatientId(match.id);
      setAmount(clinicSettings.defaultPaymentAmount || 0);
      setTotalDue(clinicSettings.defaultPaymentAmount || 0);
    }
  };

  const patientAppointments = useMemo(() => {
    const base = appointments.filter(a => a.patientId === selectedPatientId);
    if (editingPayment) {
      const appt = appointments.find(a => a.id === editingPayment.appointmentId);
      if (appt && !base.some(b => b.id === appt.id)) {
        return [appt, ...base];
      }
    }
    return base;
  }, [appointments, selectedPatientId, editingPayment]);

  useEffect(() => {
    if (patientAppointments.length > 0) {
      if (!selectedApptId) {
        const first = patientAppointments[0];
        const defaultAmount = clinicSettings.defaultPaymentAmount || 0;
        setSelectedApptId(first.id);
        setAmount(defaultAmount);
        setTotalDue(defaultAmount);
      }
    } else {
      setSelectedApptId('');
    }
  }, [patientAppointments, clinicSettings.defaultPaymentAmount, selectedApptId]);

  const selectedAppt = patientAppointments.find(a => a.id === selectedApptId);
  const submitLabel = editingId ? 'Update payment' : 'Record payment';
  const editingBanner = editingPayment ? `${editingPayment.createdAt.split('T')[0]} - $${editingPayment.amount.toFixed(2)} (${editingPayment.method})` : '';

  useEffect(() => {
    if (!intent) return;
    const targetAppt = intent.appointmentId ? appointments.find(a => a.id === intent.appointmentId) : null;
    const targetPatientId = intent.patientId || targetAppt?.patientId;

    if (targetPatientId) {
      setSelectedPatientId(targetPatientId);
      setHistoryPatientId(targetPatientId);
      const patient = patients.find(p => p.id === targetPatientId);
      if (patient) {
        setPatientSearch(patient.name);
      }
    }

    if (intent.appointmentId) {
      setSelectedApptId(intent.appointmentId);
    }

    if (typeof intent.amount === 'number') {
      setAmount(intent.amount);
      setTotalDue(intent.amount);
    }

    onConsumeIntent?.();
  }, [intent, appointments, patients, onConsumeIntent]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatientId || !selectedApptId) return;
    if (editingId) {
      const existing = payments.find(p => p.id === editingId);
      if (!existing) return;
      onUpdatePayment({
        id: editingId,
        createdAt: existing.createdAt,
        patientId: selectedPatientId,
        appointmentId: selectedApptId,
        amount,
        totalDue,
        method,
        note,
      });
      setSuccessMessage(`Payment of $${amount.toFixed(2)} updated.`);
    } else {
      onRecordPayment({
        patientId: selectedPatientId,
        appointmentId: selectedApptId,
        amount,
        totalDue,
        method,
        note,
      });
      setSuccessMessage(`Payment of $${amount.toFixed(2)} recorded for appointment ${selectedApptId}.`);
    }
    setEditingId(null);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const patientPayments = useMemo(() => {
    if (historyPatientId === 'all') return payments;
    return payments.filter(p => p.patientId === historyPatientId);
  }, [payments, historyPatientId]);

  const filteredPatientPayments = useMemo(() => {
    return patientPayments.filter(p => {
      if (startDate && p.createdAt.split('T')[0] < startDate) return false;
      if (endDate && p.createdAt.split('T')[0] > endDate) return false;
      return true;
    });
  }, [patientPayments, startDate, endDate]);

  const totalFiltered = filteredPatientPayments.reduce((sum, p) => sum + p.amount, 0);
  const groupedPayments = useMemo(() => {
    const map = new Map<string, PaymentRecord[]>();
    filteredPatientPayments.forEach(pay => {
      const key = pay.appointmentId || 'general';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)?.push(pay);
    });
    return Array.from(map.entries()).map(([appointmentId, entries]) => {
      const paid = entries.reduce((sum, p) => sum + p.amount, 0);
      const due = Math.max(
        clinicSettings.defaultPaymentAmount || 0,
        ...entries.map(p => p.totalDue ?? p.amount)
      );
      return {
        appointmentId,
        payments: entries,
        paid,
        due,
        remaining: Math.max(due - paid, 0),
      };
    });
  }, [filteredPatientPayments, clinicSettings.defaultPaymentAmount]);
  const paymentsForSelectedAppt = useMemo(
    () => payments.filter(p => p.appointmentId === selectedApptId),
    [payments, selectedApptId]
  );
  const patientMethodTotals = useMemo(() => {
    if (!selectedPatientId) return {};
    const entries = payments
      .filter(p => p.patientId === selectedPatientId)
      .reduce<Record<string, { amount: number; count: number }>>((acc, p) => {
        acc[p.method] = acc[p.method] || { amount: 0, count: 0 };
        acc[p.method].amount += p.amount;
        acc[p.method].count += 1;
        return acc;
      }, {});
    return entries;
  }, [payments, selectedPatientId]);

  const paidForAppt = paymentsForSelectedAppt
    .filter(p => p.id !== editingId)
    .reduce((sum, p) => sum + p.amount, 0);
  const targetDue = Math.max(
    totalDue || 0,
    ...paymentsForSelectedAppt.map(p => p.totalDue ?? p.amount),
    clinicSettings.defaultPaymentAmount || 0
  );
  const projectedPaid = paidForAppt + (amount || 0);
  const projectedRemaining = Math.max(targetDue - projectedPaid, 0);
  const remainingChipClass =
    projectedRemaining > 0
      ? 'border border-rose-200 bg-rose-100 text-rose-700'
      : 'border border-emerald-200 bg-emerald-100 text-emerald-700';

  const outstandingByAppt = useMemo(() => {
    const map = new Map<string, { paid: number; due: number; remaining: number }>();
    appointments
      .filter(a => a.patientId === selectedPatientId)
      .forEach(appt => {
        const apptPayments = payments.filter(p => p.appointmentId === appt.id);
        const paid = apptPayments.reduce((sum, p) => sum + p.amount, 0);
        const due = Math.max(
          clinicSettings.defaultPaymentAmount || 0,
          ...apptPayments.map(p => p.totalDue ?? p.amount)
        );
        map.set(appt.id, { paid, due, remaining: Math.max(due - paid, 0) });
      });
    return map;
  }, [appointments, payments, selectedPatientId, clinicSettings.defaultPaymentAmount]);

  const remainingForPatient = useMemo(() => {
    const appts = appointments.filter(a => a.patientId === selectedPatientId);
    if (appts.length === 0) return 0;
    return appts.reduce((sum, appt) => {
      const info = outstandingByAppt.get(appt.id);
      return sum + (info?.remaining ?? Math.max((clinicSettings.defaultPaymentAmount || 0) - (info?.paid ?? 0), 0));
    }, 0);
  }, [appointments, selectedPatientId, outstandingByAppt, clinicSettings.defaultPaymentAmount]);

  const escapeCsv = (value: string) => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleExport = () => {
    if (filteredPatientPayments.length === 0) return;
    const lines = [
      ['Date', 'Patient', 'Appointment', 'Amount', 'Method', 'Note'].join(','),
      ...filteredPatientPayments.map(pay => {
        const patient = patients.find(p => p.id === pay.patientId);
        const appt = appointments.find(a => a.id === pay.appointmentId);
        const row = [
          pay.createdAt.split('T')[0],
          patient?.name || pay.patientId,
          appt ? `${appt.date} ${appt.time} - ${appt.type}` : pay.appointmentId,
          pay.amount.toFixed(2),
          pay.method,
          pay.note || '',
        ];
        return row.map(escapeCsv).join(',');
      }),
    ].join('\n');

    const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'payments.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-black">Collections</h2>
          <p className="text-sm text-slate-500">Collect patient fees after treatment.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 font-bold">
            Default: ${clinicSettings.defaultPaymentAmount ?? 0}
          </span>
          <span className="px-3 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-bold">
            Range total: ${totalFiltered.toFixed(2)}
          </span>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className={`bg-white rounded-3xl border shadow-sm p-6 space-y-5 transition-all animate-scaleIn ${editingId ? 'ring-2 ring-blue-500' : ''
          }`}
      >
        <div className="flex items-center justify-between text-xs text-slate-600">
          <span className="font-bold">Remaining for patient:</span>
          <span className={`font-black ${remainingForPatient > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
            ${remainingForPatient.toFixed(2)}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Patient</label>
            {editingId ? (
              <div className="p-3 rounded-xl border bg-slate-50">
                <p className="font-bold text-slate-800">{getPatientName(selectedPatientId) || 'Patient'}</p>
                <p className="text-xs text-slate-500">Editing payment; cancel edit to change patient.</p>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="w-full border rounded-xl p-3 text-sm"
                  list="patient-options"
                  placeholder="Type to search and select patient..."
                  value={patientSearch}
                  onChange={(e) => handlePatientInputChange(e.target.value)}
                />
                <datalist id="patient-options">
                  {filteredPatients.map(p => (
                    <option key={p.id} value={p.name}>
                      {p.email || p.phone}
                    </option>
                  ))}
                </datalist>
              </>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Appointment</label>
            <select
              className="w-full border rounded-xl p-3 text-sm"
              value={selectedApptId || selectedAppt?.id || ''}
              onChange={(e) => setSelectedApptId(e.target.value)}
              disabled={!selectedPatientId}
            >
              <option value="">General payment (no appointment)</option>
              {selectedPatientId && patientAppointments.length === 0 && <option value="" disabled>No appointments</option>}
              {selectedPatientId &&
                patientAppointments.map(a => {
                  const dentist = dentists.find(d => d.id === a.providerId);
                  const outstanding = outstandingByAppt.get(a.id);
                  return (
                    <option key={a.id} value={a.id}>
                      {a.date} {a.time} - {a.type} with {dentist?.name || 'Provider'} {outstanding ? `(Remaining $${outstanding.remaining.toFixed(2)})` : ''}
                    </option>
                  );
                })}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Amount</label>
            <input
              type="number"
              min={0}
              step={1}
              className="w-full border rounded-xl p-3 text-sm"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Total due for this appointment</label>
            <input
              type="number"
              min={0}
              step={1}
              className="w-full border rounded-xl p-3 text-sm"
              value={totalDue}
              onChange={(e) => setTotalDue(parseFloat(e.target.value) || 0)}
            />
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] font-black bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-amber-800 shadow-sm">
              <Icons.AlertCircle />
              <span className="flex items-center gap-1">
                Paid so far:
                <span className="px-2 py-1 rounded-lg bg-white border border-amber-200 text-amber-800">
                  ${paidForAppt.toFixed(2)}
                </span>
              </span>
              <span className="flex items-center gap-1">
                Remaining after save:
                <span className={`px-2 py-1 rounded-lg ${remainingChipClass}`}>
                  ${projectedRemaining.toFixed(2)}
                </span>
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Method</label>
            <div className="flex gap-2">
              {(['cash', 'card', 'insurance', 'installment'] as PaymentMethod[]).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={`flex-1 px-3 py-2 rounded-xl border text-sm font-bold ${method === m ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-50 text-slate-600 border-slate-200'
                    }`}
                >
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {editingPayment && (
          <div className="p-3 rounded-xl bg-blue-50 text-blue-700 text-xs font-bold flex items-center justify-between">
            <span>Editing payment {editingPayment.id} — {editingBanner}</span>
            <button
              type="button"
              className="px-3 py-1 rounded-lg bg-white border border-blue-200 text-blue-700 hover:bg-blue-100"
              onClick={() => setEditingId(null)}
            >
              Cancel edit
            </button>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs font-bold text-slate-500 uppercase">Notes</label>
          <textarea
            className="w-full border rounded-xl p-3 text-sm"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add any billing notes or reference."
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-slate-400">Make sure payment matches clinic defaults or adjust as needed.</div>
          <div className="flex items-center gap-2">
            {editingId && (
              <button
                type="button"
                className="px-4 py-3 rounded-xl border text-sm font-bold hover:bg-slate-50"
                onClick={() => setEditingId(null)}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-5 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700"
              disabled={!selectedPatientId}
            >
              {submitLabel}
            </button>
          </div>
        </div>

        {successMessage && (
          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-700 text-sm flex items-center gap-2">
            <Icons.CheckCircle />
            <span>{successMessage}</span>
          </div>
        )}
      </form>

      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white rounded-3xl border shadow-sm p-4 space-y-3 animate-fadeIn">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-black">Patient payment history</h3>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 font-bold">{filteredPatientPayments.length} payments</span>
              <select
                className="border rounded-lg px-2 py-1"
                value={historyPatientId}
                onChange={(e) => setHistoryPatientId(e.target.value)}
              >
                <option value="all">All patients</option>
                {patients.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <label className="flex items-center gap-1">
                Start
                <input type="date" className="border rounded-lg px-2 py-1" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label className="flex items-center gap-1">
                End
                <input type="date" className="border rounded-lg px-2 py-1" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
              <button
                type="button"
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleExport}
                className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 flex items-center gap-1"
                disabled={filteredPatientPayments.length === 0}
              >
                <Icons.Download />
                Export CSV
              </button>
            </div>
          </div>
          {selectedPatientId && Object.keys(patientMethodTotals).length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs text-slate-600">
              {Object.entries(patientMethodTotals).map(([method, data]) => (
                <span key={method} className="px-3 py-1 rounded-lg bg-slate-100 border font-bold capitalize">
                  {method}: ${data.amount.toFixed(2)} ({data.count})
                </span>
              ))}
            </div>
          )}
          {filteredPatientPayments.length === 0 ? (
            <p className="text-sm text-slate-400">No payments for this patient yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[11px]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Date</th>
                    <th className="text-left px-3 py-2 font-bold">Appointment / Installment</th>
                    <th className="text-left px-3 py-2 font-bold">Amount</th>
                    <th className="text-left px-3 py-2 font-bold">Method</th>
                    <th className="text-left px-3 py-2 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedPayments.map(group => {
                    const appt = appointments.find(a => a.id === group.appointmentId);
                    const appointmentLabel = appt ? `${appt.date} ${appt.time} - ${appt.type}` : 'General payment';
                    return (
                      <React.Fragment key={group.appointmentId}>
                        <tr className="border-b bg-slate-50">
                          <td className="px-3 py-2 font-bold text-slate-700" colSpan={5}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="font-black">{appointmentLabel}</span>
                                <span className="text-xs text-slate-500">({group.payments.length} payments)</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs font-bold">
                                <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700">
                                  Paid ${group.paid.toFixed(2)}
                                </span>
                                <span className="px-2 py-1 rounded-lg bg-blue-50 text-blue-700">
                                  Due ${group.due.toFixed(2)}
                                </span>
                                <span className={`px-2 py-1 rounded-lg ${group.remaining > 0 ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                                  Remaining ${group.remaining.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {group.payments.map((pay, index) => (
                          <tr key={pay.id} className={`border-b last:border-b-0 ${editingId === pay.id ? 'bg-blue-50/60' : ''}`}>
                            <td className="px-3 py-2">{pay.createdAt.split('T')[0]}</td>
                            <td className="px-3 py-2 text-slate-600">
                              <div className="font-semibold">
                                {pay.method === 'installment' ? `Installment ${index + 1}` : `Payment ${index + 1}`}
                              </div>
                              {pay.note && <div className="text-xs text-slate-400">{pay.note}</div>}
                            </td>
                            <td className="px-3 py-2 font-bold">${pay.amount.toFixed(2)}</td>
                            <td className="px-3 py-2 capitalize">{pay.method}</td>
                            <td className="px-3 py-2 text-right space-x-2">
                              <button
                                type="button"
                                className="px-2 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                                onClick={() => {
                                  setSelectedPatientId(pay.patientId);
                                  setSelectedApptId(pay.appointmentId);
                                  setAmount(pay.amount);
                                  setMethod(pay.method);
                                  setNote(pay.note || '');
                                  setEditingId(pay.id);
                                  setHistoryPatientId(pay.patientId);
                                  setConfirmDeleteId(null);
                                  setPatientSearch(getPatientName(pay.patientId));
                                }}
                              >
                                Edit
                              </button>
                              {confirmDeleteId === pay.id ? (
                                <>
                                  <button
                                    type="button"
                                    className="px-2 py-1 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700"
                                    onClick={() => {
                                      onDeletePayment(pay.id);
                                      setConfirmDeleteId(null);
                                      setSuccessMessage('Payment deleted');
                                      setTimeout(() => setSuccessMessage(''), 3000);
                                    }}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    className="px-2 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                                    onClick={() => setConfirmDeleteId(null)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="px-2 py-1 rounded-lg bg-red-100 text-red-700 text-xs font-bold hover:bg-red-200"
                                  onClick={() => setConfirmDeleteId(pay.id)}
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};


